import React, { useCallback, useContext, useState } from 'react';
import { Paper, Button, StepLabel, Step, StepContent, Stepper } from '@material-ui/core';
import clsx from 'clsx';
import produce from 'immer';
import { MonitorTargetType, Monitor, MonitorAction, MonitorCondition, MonitorSeverity, EditableMonitorData, RobotDefinition, SupportedTicketSystems,
    MissingDataTreatment,
    SlackSettings } from '@moderepo/mode-apis';
import { useModePanelStyle, useModeFormStyle, BaseInfoCompProps, FontIcon, BaseInfoCompActionsSet, BaseInfoCompSaveAction, capitalizeAll,
    capitalizeFirst } from '@moderepo/mode-app-components';
import { ritzDataContext, selectProjectById } from '@moderepo/mode-data-state-ritz';
import { rcDataContext, selectRobotById } from '@moderepo/mode-data-state-robot-cloud';
import { useRitzTranslation } from '../../../utils/customHooks';
import { useEditMonitorStyle } from './EditMonitorStyle';
import { EditMonitorInfoStep } from './EditMonitorInfoStep';
import { EditMonitorTargetStep } from './EditMonitorTargetStep';
import { EditMonitorConditionsStep } from './EditMonitorConditionsStep';
import { EditMonitorActionsStep } from './EditMonitorActionsStep';



export enum CreateMonitorStep {
    INFO = 'info',
    TARGET = 'target',
    CONDITIONS = 'conditions',
    ACTIONS = 'actions',
}


interface StepConfig {
    readonly name: CreateMonitorStep;
    readonly label: string;
}


/**
 * This is the interface of the set of input fields' values that will be passed to the onClick handler when the 'saveUpdate' button is clicked.
 */
export interface RCMonitorEditorUpdatableInputs {
    readonly data: EditableMonitorData;
    readonly monitorId?: number | undefined;
}


/**
 * This component has a custom saveUpdate button's onClick input params therefore we need to extends the BaseInfoCompActionsSet and
 * override the 'saveUpdate' action.
 */
export interface RCMonitorEditorActionsSet extends BaseInfoCompActionsSet {
    readonly saveUpdate: BaseInfoCompSaveAction<RCMonitorEditorUpdatableInputs>
}



interface RCMonitorEditorProps extends BaseInfoCompProps {
    readonly projectId: number;

    readonly monitor?: Monitor;                   // The monitor object that the user is editing. Undefined if this is for creating a new monitor
    readonly preSelectedRobotClass?: string;
    readonly preSelectedRobotId?: string;
    readonly preSelectedHomeId?: number;

    readonly robotCatalog: readonly RobotDefinition[] | undefined,
    readonly supportedTicketSystems?: SupportedTicketSystems| undefined;
    readonly slackChannels?: SlackSettings['channels'] | undefined;
    readonly jumpToStep?: CreateMonitorStep | undefined;

    readonly actions?: RCMonitorEditorActionsSet | undefined;
}


const steps: StepConfig[] = [{
    name : CreateMonitorStep.INFO,
    label: 'create_monitor.step_info.title',
}, {
    name : CreateMonitorStep.TARGET,
    label: 'create_monitor.step_target.title',
}, {
    name : CreateMonitorStep.CONDITIONS,
    label: 'create_monitor.step_conditions.title',
}, {
    name : CreateMonitorStep.ACTIONS,
    label: 'create_monitor.step_actions.title',
}];



/**
 * This is a DUMB component used for displaying alert info. This component does not have any logic. All it does it display
 * data based on the props provided by the container. All action done in this component will be dispatched back to the
 * container's handler.
 */
export const RCMonitorEditor: React.FC<RCMonitorEditorProps> = (props: RCMonitorEditorProps) => {

    const { state:dataState } = useContext(ritzDataContext);
    const { state:rcDataState } = useContext(rcDataContext);
    const project = selectProjectById(dataState, props.projectId);
    const { trans } = useRitzTranslation(project);

    const [currentStep, setCurrentStep] = useState<number>(props.jumpToStep ? steps.findIndex((s: StepConfig) => {
        return s.name === props.jumpToStep;
    }) : 0);



    // To keep track of the errors for each step so we know which step has error
    const [errorsByStepName, setErrorsByStepName] = useState<{[step: string]: boolean}>({});



    // The saved input values after each step is completed
    const [monitorName, setMonitorName] = useState<string | undefined>(props.monitor?.name);
    const [monitorDescription, setMonitorDescription] = useState<string | undefined>(props.monitor?.description);
    const [monitorSeverity, setMonitorSeverity] = useState<MonitorSeverity | undefined>(props.monitor?.severity);
    const [monitorTargetType, setMonitorTargetType] = useState<MonitorTargetType | undefined>(
        props.preSelectedRobotClass || props.preSelectedRobotId
            ? (props.preSelectedRobotClass && MonitorTargetType.ROBOT_CLASS) || MonitorTargetType.ROBOT_ID
            : props.monitor?.targetType,
    );

    // If there are preSelected robotClass or robotId, use the pre selected values. If not, use the Alert object's value
    const [targetRobotClass, setTargetRobotClass] = useState<string | undefined>(
        props.preSelectedRobotClass ? props.preSelectedRobotClass : props.monitor?.targetRobotClass,
    );
    const [targetRobotId, setTargetRobotId] = useState<string | undefined>(
        props.preSelectedRobotId && props.preSelectedHomeId ? props.preSelectedRobotId : props.monitor?.targetRobotId,
    );
    const [targetHomeId, setTargetHomeId] = useState<number | undefined>(
        (props.preSelectedRobotClass || props.preSelectedRobotId) && props.preSelectedHomeId ? props.preSelectedHomeId : props.monitor?.targetHomeId,
    );

    const [monitorCondition, setMonitorCondition] = useState<MonitorCondition | undefined>(props.monitor?.condition);
    const [treatMissingDataAs, setTreatMissingDataAs] = useState<MissingDataTreatment | undefined>(props.monitor?.treatMissingDataAs);
    const [sampleInterval, setSampleInterval] = useState<number | undefined>(props.monitor?.sampleInterval);
    const [monitorActions, setMonitorActions] = useState<MonitorAction[] | undefined>(
        props.monitor && props.monitor.actions ? [...props.monitor.actions] : undefined,
    );


    // If the targetType is RobotId
    const targetRobot = selectRobotById(rcDataState, targetHomeId, targetRobotId);

    const panelClasses = useModePanelStyle();
    const formClasses = useModeFormStyle();
    const classes = useEditMonitorStyle();



    /**
     * On Prev step button clicked. Take the user to the previous step but make sure the current step does not have any error
     */
    const onPrevStep = useCallback(() => {
        setCurrentStep((prevStep: number) => {
            return prevStep - 1;
        });
    }, []);



    /**
     * On Next step button clicked. Take the user to the next step but make sure the current step does not have any error
     */
    const onNextStep = useCallback(() => {
        const stepConfig = steps[currentStep];
        if (!stepConfig || !errorsByStepName[stepConfig.name]) {
            setCurrentStep((prevStep: number) => {
                return prevStep + 1;
            });
        }
    }, [currentStep, errorsByStepName]);


    /**
     * On go to the end clicked. Need to make sure none of the step contain errors
     */
    const onGotoLastStep = useCallback(() => {
        if (Object.values(errorsByStepName).find((errors: boolean | undefined) => {
            return errors;
        }) === undefined) {
            setCurrentStep(steps.length);
        }
    }, [errorsByStepName]);


    /**
     * On Step click, we can take the user directly to that step BUT we will only allow the user to go to a specific step when the user finished
     * all the steps
     */
    const onStepClick = useCallback((index: number) => {
        if (currentStep >= steps.length) {
            setCurrentStep(index);
        }
    }, [currentStep]);



    /**
     * onStepCompleted handler for the MonitorInfo step. We will get the updatedAlertName and will save it locally.
     */
    const onMonitorInfoStepCompleted = useCallback((
        updatedAlertName: string, updatedAlertDescription: string, updatedMonitorSeverity: MonitorSeverity,
    ) => {
        setMonitorName(updatedAlertName);
        setMonitorDescription(updatedAlertDescription);
        setMonitorSeverity(updatedMonitorSeverity);
    }, []);


    /**
     * onStepCompleted handler for the Monitor Target step. We will get the target type, target robot class, and/or target robot id
     */
    const onMonitorTargetStepCompleted = useCallback((
        type: MonitorTargetType, robotClass: string | undefined, robotId: string | undefined, homeId: number | undefined,
    ) => {
        setMonitorTargetType(type);
        setTargetRobotClass(robotClass);
        setTargetRobotId(robotId);
        setTargetHomeId(homeId);
    }, []);


    /**
     * onStepCompleted handler for the monitor Condition step. We will get the conditions' valueName, comparison operator, and value
     */
    const onMonitorConditionsStepCompleted = useCallback((
        condition: MonitorCondition, missingDataTreatment: MissingDataTreatment, sampleInt: number,
    ) => {
        setMonitorCondition(condition);
        setTreatMissingDataAs(missingDataTreatment);
        setSampleInterval(sampleInt);
    }, []);


    const onMonitorActionsStepCompleted = useCallback((actions: MonitorAction[]) => {
        setMonitorActions(actions);
    }, []);


    /**
     * On submit button clicked, call the onFormSubmit handler and pass it the user input values
     */
    const onFormSubmit = useCallback(async (event: React.FormEvent) => {
        event.stopPropagation();
        event.preventDefault();

        if (props.actions?.saveUpdate) {
            props.actions.saveUpdate.onClick({
                data: {
                    name              : monitorName as string,
                    description       : monitorDescription as string,
                    severity          : monitorSeverity as MonitorSeverity,
                    targetType        : monitorTargetType as MonitorTargetType,
                    targetRobotClass  : monitorTargetType === MonitorTargetType.ROBOT_CLASS ? targetRobotClass as string : '',
                    targetRobotId     : monitorTargetType === MonitorTargetType.ROBOT_ID ? targetRobotId as string : '',
                    targetHomeId      : targetHomeId ? targetHomeId as number : 0,
                    condition         : monitorCondition as MonitorCondition,
                    treatMissingDataAs: treatMissingDataAs as MissingDataTreatment,
                    sampleInterval    : sampleInterval as number,
                    actions           : monitorActions as readonly MonitorAction[],
                },
                monitorId: props.monitor?.id,
            });
        }

        return false;
    }, [props, monitorName, monitorDescription, monitorTargetType, targetRobotClass, targetRobotId, monitorCondition, monitorActions, monitorSeverity,
        treatMissingDataAs, sampleInterval, targetHomeId]);



    /**
     * Check if the form can be saved. We will use this flag to enable the Save button.
     * A form can be saved if it met all these conditions
     */
    const canSaveForm: boolean = (
        currentStep >= steps.length                                                                         // We passed the last step
        && Object.values(errorsByStepName).find((hasError: boolean): boolean => {                           // None of the step has error
            return hasError;
        }) === undefined
        && monitorName !== undefined
        && monitorSeverity !== undefined
        && monitorTargetType !== undefined
        && ((monitorTargetType === MonitorTargetType.ROBOT_CLASS && targetRobotClass !== undefined)
            || (monitorTargetType === MonitorTargetType.ROBOT_ID && targetRobotId !== undefined))
        && monitorCondition !== undefined
        && treatMissingDataAs !== undefined
        && sampleInterval !== undefined
        && monitorActions !== undefined && monitorActions.length > 0
    );



    return (
        <Paper
            elevation={2}
            className={clsx(panelClasses.root, props.className)}
        >
            <div className={clsx(panelClasses.panelHeader)}>
                <div className={clsx(panelClasses.panelHeaderIconContainer)}>
                    {props.icon && <FontIcon className={clsx(panelClasses.panelHeaderIcon)} iconName={props.icon} />}
                </div>
                <div className={panelClasses.panelHeaderTitle}>
                    {props.title}
                </div>
            </div>
            <div className={clsx(panelClasses.panelContent, classes.panelContent)}>
                {props.robotCatalog && props.robotCatalog.length > 0 ? (
                    <form
                        className={
                            clsx(formClasses.root, props.formProps?.className)
                        }
                        noValidate
                        autoComplete="on"
                        method="POST"
                        onSubmit={onFormSubmit}
                    >
                        <Stepper
                            className={classes.stepper}
                            activeStep={currentStep}
                            orientation="vertical"
                        >
                            {steps.map((stepConfig: StepConfig, index: number) => {
                                return (
                                    <Step className={classes.step} key={stepConfig.label}>
                                        <StepLabel
                                            className={classes.stepLabel}
                                            error={Boolean(errorsByStepName[stepConfig.name])}
                                            optional={errorsByStepName[stepConfig.name] ? trans('label.monitor_config_step_error_message') : ''}
                                            onClick={() => {
                                                onStepClick(index);
                                            }}
                                        >
                                            {capitalizeAll(trans(stepConfig.label))}
                                        </StepLabel>
                                        <StepContent className={classes.stepContent}>
                                            {stepConfig.name === CreateMonitorStep.INFO && (
                                                <EditMonitorInfoStep
                                                    projectId={props.projectId}
                                                    monitorName={monitorName}
                                                    monitorDescription={monitorDescription}
                                                    monitorSeverity={monitorSeverity}
                                                    trans={trans}
                                                    isNewMonitor={!props.monitor}
                                                    canGoBack={index > 0}
                                                    isLastStep={currentStep === steps.length - 1}
                                                    onPrevStepClick={onPrevStep}
                                                    onNextStepClick={onNextStep}
                                                    onStepError={(hasErrors: boolean) => {
                                                        setErrorsByStepName(produce(errorsByStepName, (draft: any) => {
                                                            draft[stepConfig.name] = hasErrors;
                                                        }));
                                                    }}
                                                    onStepCompleted={onMonitorInfoStepCompleted}
                                                    onGotoLastStepClick={onGotoLastStep}
                                                />
                                            )}

                                            {stepConfig.name === CreateMonitorStep.TARGET && (
                                                <EditMonitorTargetStep
                                                    preSelected={Boolean(
                                                        props.preSelectedRobotClass || props.preSelectedRobotId,
                                                    )}
                                                    projectId={props.projectId}
                                                    robotCatalog={props.robotCatalog}
                                                    monitorTargetType={monitorTargetType}
                                                    targetRobotClass={targetRobotClass}
                                                    targetRobotId={targetRobotId}
                                                    targetHomeId={targetHomeId}
                                                    trans={trans}
                                                    isNewMonitor={!props.monitor}
                                                    canGoBack={index > 0}
                                                    isLastStep={currentStep === steps.length - 1}
                                                    onPrevStepClick={onPrevStep}
                                                    onNextStepClick={onNextStep}
                                                    onStepError={(hasErrors: boolean) => {
                                                        setErrorsByStepName(produce(errorsByStepName, (draft: any) => {
                                                            draft[stepConfig.name] = hasErrors;
                                                        }));
                                                    }}
                                                    onStepCompleted={onMonitorTargetStepCompleted}
                                                    onGotoLastStepClick={onGotoLastStep}
                                                />
                                            )}

                                            {stepConfig.name === CreateMonitorStep.CONDITIONS && (
                                                <EditMonitorConditionsStep
                                                    projectId={props.projectId}
                                                    robotCatalog={props.robotCatalog}
                                                    targetRobotClass={targetRobotClass}
                                                    targetRobot={targetRobot}
                                                    condition={monitorCondition}
                                                    treatMissingDataAs={treatMissingDataAs}
                                                    sampleInterval={sampleInterval}
                                                    trans={trans}
                                                    isNewMonitor={!props.monitor}
                                                    canGoBack={index > 0}
                                                    isLastStep={currentStep === steps.length - 1}
                                                    onPrevStepClick={onPrevStep}
                                                    onNextStepClick={onNextStep}
                                                    onStepError={(hasErrors: boolean) => {
                                                        setErrorsByStepName(produce(errorsByStepName, (draft: any) => {
                                                            draft[stepConfig.name] = hasErrors;
                                                        }));
                                                    }}
                                                    onStepCompleted={onMonitorConditionsStepCompleted}
                                                    onGotoLastStepClick={onGotoLastStep}
                                                />
                                            )}

                                            {stepConfig.name === CreateMonitorStep.ACTIONS && (
                                                <EditMonitorActionsStep
                                                    projectId={props.projectId}
                                                    actions={monitorActions}
                                                    trans={trans}
                                                    supportedTicketSystems={props.supportedTicketSystems}
                                                    slackChannels={props.slackChannels}
                                                    isNewMonitor={!props.monitor}
                                                    canGoBack={index > 0}
                                                    isLastStep={currentStep === steps.length - 1}
                                                    onPrevStepClick={onPrevStep}
                                                    onNextStepClick={onNextStep}
                                                    onStepError={(hasErrors: boolean) => {
                                                        setErrorsByStepName(produce(errorsByStepName, (draft: any) => {
                                                            draft[stepConfig.name] = hasErrors;
                                                        }));
                                                    }}
                                                    onStepCompleted={onMonitorActionsStepCompleted}
                                                    onGotoLastStepClick={onGotoLastStep}
                                                />
                                            )}

                                        </StepContent>
                                    </Step>
                                );

                            })}
                        </Stepper>

                        {props.isEditing && (
                            <div className={clsx(formClasses.formActionContainer)}>
                                <div className={classes.allStepsCompletedMessage}>
                                    {currentStep >= steps.length && (
                                        trans('create_monitor.all_steps_completed_message')
                                    )}
                                </div>
                                <div>
                                    {props.actions?.cancelEdit && (
                                        <Button
                                            className={clsx(formClasses.formActionButton)}
                                            size="small"
                                            startIcon={props.actions.cancelEdit.icon
                                                ? <FontIcon iconName={props.actions.cancelEdit.icon} /> : undefined}
                                            onClick={() => {
                                                if (props.actions?.cancelEdit) {
                                                    props.actions.cancelEdit.onClick();
                                                }
                                            }}
                                        >
                                            {props.actions.cancelEdit.label}
                                        </Button>
                                    )}
                                    {props.actions?.saveUpdate && (
                                        <Button
                                            className={clsx(formClasses.formActionButton)}
                                            variant="contained"
                                            color="primary"
                                            type="submit"
                                            size="small"
                                            disabled={!canSaveForm}
                                            startIcon={props.actions.saveUpdate.icon
                                                ? <FontIcon iconName={props.actions.saveUpdate.icon} /> : undefined}
                                        >
                                            {props.actions.saveUpdate.label}
                                        </Button>
                                    )}
                                </div>
                            </div>
                        )}
                    </form>
                ) : (
                    <div>
                        {capitalizeFirst(trans('create_monitor.missing_required_data_message'))}
                    </div>
                )}
            </div>

        </Paper>
    );

};
